import React from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Zap, Sparkles, TrendingUp } from "lucide-react";

const WorkflowLimitBlock = ({
  currentCount = 10,
  maxLimit = 10,
  onUpgrade,
}) => {
  const progress = Math.min((currentCount / maxLimit) * 100, 100);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
      className="relative w-full max-w-2xl mx-auto overflow-hidden rounded-3xl shadow-2xl border border-orange-100/50 backdrop-blur-xl bg-white/70"
    >
      {/* Animated Gradient Background */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50"
        animate={{
          background: [
            "linear-gradient(135deg,#fff7ed,#fef2f2,#fffbeb)",
            "linear-gradient(135deg,#fffbeb,#fff7ed,#fef2f2)",
          ],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Glow Orbs */}
      <motion.div
        className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-gradient-to-br from-orange-300/40 to-red-300/40 blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 6, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-0 left-0 w-56 h-56 rounded-full bg-gradient-to-tr from-amber-200/40 to-orange-300/40 blur-3xl"
        animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 8, repeat: Infinity, delay: 1 }}
      />

      <div className="relative z-10 p-8">
        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <motion.div
            className="p-4 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 shadow-lg relative"
            animate={{
              boxShadow: [
                "0 0 15px rgba(249,115,22,0.3)",
                "0 0 25px rgba(249,115,22,0.5)",
                "0 0 15px rgba(249,115,22,0.3)",
              ],
            }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            <AlertTriangle className="w-7 h-7 text-white" />
            <motion.div
              className="absolute inset-0 border-2 border-orange-300/40 rounded-2xl"
              animate={{
                scale: [1, 1.15, 1],
                opacity: [0.6, 0, 0.6],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </motion.div>

          <div className="flex-1">
            <h3 className="text-2xl font-extrabold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Workflow Limit Reached
            </h3>
            <div className="mt-1 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 border border-orange-100 shadow-sm">
              <TrendingUp className="w-3.5 h-3.5 text-orange-600" />
              <span className="text-sm font-semibold text-gray-700">
                {currentCount}/{maxLimit} used
              </span>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex justify-between text-sm font-medium text-gray-600 mb-2">
            <span>Usage Progress</span>
            <motion.span
              className="font-bold text-orange-600"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {Math.round(progress)}%
            </motion.span>
          </div>
          <div className="relative w-full h-3 rounded-full bg-gray-200/70 overflow-hidden">
            <motion.div
              className="absolute top-0 left-0 h-3 rounded-full bg-gradient-to-r from-orange-500 via-red-500 to-orange-600"
              initial={{ width: "0%" }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 1.2, ease: "easeOut" }}
            />
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </div>

        {/* Message */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-5 rounded-2xl bg-white/60 border border-orange-100 text-sm text-gray-700 leading-relaxed"
        >
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-orange-500 mt-0.5" />
            <p>
              You’ve hit your workflow creation limit. You can{" "}
              <span className="font-semibold text-gray-900">remove old workflows</span>{" "}
              to make space, or{" "}
              <span className="font-semibold text-gray-900">upgrade your plan</span>{" "}
              to unlock unlimited workflow generation.
            </p>
          </div>
        </motion.div>

        {/* Action Buttons */}
        {onUpgrade && (
          <motion.button
            onClick={onUpgrade}
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="relative w-full mt-6 flex items-center justify-center px-6 py-3.5 rounded-xl font-semibold text-white shadow-lg
              bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 overflow-hidden"
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent"
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
            <Zap className="w-5 h-5 mr-2 relative z-10" />
            <span className="relative z-10">Upgrade for Unlimited Access</span>
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export default WorkflowLimitBlock;
